<template>
  <!--
    Explicit full-day height. Previously this was implicit: 96 block-level
    slot divs summed to `minuteSize * 1440`px and pushed the section out.
    Now that the slots are one absolute overlay, nothing pushes the section
    size, so the `border-r` (column separator) would otherwise collapse to
    just the container's visible working-hours height.
  -->
  <div
    id="section"
    class="relative flex-shrink-0 border-r border-gray-100 bg-white dark:bg-gray-900 dark:border-gray-800"
    :style="{ width: `${sectionSize}px`, height: `${minuteSize * 1440}px` }"
  >
    <template v-if="hour && hour.working">
      <schedule-unavailable :section="section" />
    </template>
    <template v-else>
      <div class="absolute bg-gray-100 dark:bg-gray-800 w-full" :style="{ height: `${minuteSize * 1440}px` }" />
    </template>

    <!--
      Click/hover target for creating new reservations. Single full-height
      overlay — Modifier.vue reads `event.offsetY` plus the two data attrs
      below to derive which time slot was clicked. Grid lines are painted
      via the `background-image` gradient in `slotStyle` — one repeating
      pattern per section instead of 96 individual bordered divs per
      section (×N employees × days on week view). Big DOM + paint win.
      See .docs/performance.md.

      Rendered BEFORE `<schedule-block>` so reservation blocks paint on top
      of the grid lines in stacking order. `bg-clip-padding` keeps the
      gradient inside the content box so it doesn't obscure the section's
      right border (the visible column separator).
    -->
    <div
      class="slot schedule-slot absolute inset-x-0 top-0 bg-clip-padding"
      :data-slot-size="slotSize"
      :data-minute-size="minuteSize"
      :style="slotStyle"
      @mousemove="handleSlotMove"
      @mouseleave="hoveredSlot = null"
    >
      <!--
        Hover highlight band — driven by `hoveredSlot` derived from
        `event.offsetY`. Replaces the previous :hover pseudo-class on each
        individual slot div (which required 96 divs with transitions per
        section, ×N sections = expensive paint budget on every mousemove).
      -->
      <div
        v-if="hoveredSlot !== null"
        class="absolute inset-x-0 pointer-events-none bg-primary-50 border border-primary-100 dark:bg-primary-900/50 dark:border-primary-900"
        :style="hoverBandStyle"
      />
    </div>

    <schedule-block
      v-for="block in blocks"
      :key="block.hash"
      v-memo="[block.hash]"
      :block="block"
    />
  </div>
</template>

<script setup lang="ts">
import ScheduleBlock from '@/components/schedule/elements/Block.vue'
import ScheduleUnavailable from '@/components/schedule/elements/Unavailable.vue'

import { useUIStore } from '~/stores/useUIStore'

const { $auth } = useNuxtApp()
const uiStore = useUIStore()

interface Props {
  section: any
  provider: any
}

const props = defineProps<Props>()

const blocks = computed(() => props.section.blocks)
const hour = computed(() => props.section.roster?.hour)
const minuteSize = computed(() => {
  const value = Number($auth?.info?.minuteSize)
  return Number.isFinite(value) && value > 0 ? value : 2
})
const sectionSize = computed(() => uiStore.schedule.sectionSize)
const slotSize = computed(() => {
  const value = Number($auth?.info?.slotSize)
  return Number.isFinite(value) && value > 0 ? value : 15
})

const slotHeight = computed(() => minuteSize.value * slotSize.value)

const slotStyle = computed(() => {
  const slotPx = slotHeight.value
  // Draw a 1px line at the bottom of every `slotPx`-high tile. Colour via
  // CSS var so dark mode is handled in the `<style>` block without rebuilding
  // this gradient on every palette change.
  return {
    height: `${minuteSize.value * 1440}px`,
    backgroundImage: `repeating-linear-gradient(to bottom, transparent 0, transparent ${slotPx - 1}px, var(--schedule-slot-line) ${slotPx - 1}px, var(--schedule-slot-line) ${slotPx}px)`,
  }
})

const hoveredSlot = ref<number | null>(null)

const hoverBandStyle = computed(() => {
  if (hoveredSlot.value === null) return {}
  return {
    top: `${hoveredSlot.value * slotHeight.value}px`,
    height: `${slotHeight.value}px`,
  }
})

function handleSlotMove(event: MouseEvent) {
  const px = slotHeight.value
  if (px <= 0) return
  const next = Math.floor(event.offsetY / px)
  if (next !== hoveredSlot.value) hoveredSlot.value = next
}
</script>

<style scoped>
.schedule-slot {
  /* The old grid used dashed borders, so soften the solid gradient to retain
     the same visual weight while keeping the single-overlay paint path. */
  --schedule-slot-line: rgb(var(--raw-gray-100) / 0.5);
}

:global(.dark) .schedule-slot {
  --schedule-slot-line: rgb(var(--raw-gray-800) / 0.5);
}
</style>
